"use client";

import React, { useEffect, useMemo } from "react";
import { BookOpen, ShieldCheck, Sparkles, Info, X, AlertTriangle, Loader2 } from "lucide-react";

const CSS = `
.pim-overlay{
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0,0,0,.55);
  display:flex;
  align-items:center;
  justify-content:center;
  padding: 18px;
}
.pim-card{
  width: min(920px, 100%);
  max-height: min(88vh, 780px); 
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 22px 60px rgba(0,0,0,.22);
  overflow: hidden;
  border: 1px solid rgba(0,0,0,.06);
  display:flex;
  flex-direction: column;
}

@media (max-width: 640px){
  .pim-card{
    max-height: 92vh;
    border-radius: 12px;
  }
}
.pim-head{
  padding: 18px 18px 12px;
  border-bottom: 1px solid rgba(0,0,0,.08);
  display:flex;
  gap: 14px;
  align-items:flex-start;
  justify-content: space-between;
}
.pim-titleWrap{ display:flex; gap: 12px; align-items:flex-start; }
.pim-badge{
  width: 40px; height: 40px;
  border-radius: 12px;
  background: rgba(76,175,80,.12);
  border: 1px solid rgba(76,175,80,.25);
  display:flex; align-items:center; justify-content:center;
  flex: 0 0 auto;
  margin-top: 2px;
}
.pim-title{
  margin: 0;
  font-size: 1.15rem;
  color: #111827;
  line-height: 1.2;
}
.pim-sub{
  margin: 6px 0 0;
  color:#6b7280;
  font-size: .95rem;
}

.pim-close{
  border: 0;
  background: transparent;
  padding: 8px;
  border-radius: 10px;
  cursor: pointer;
  color:#6b7280;
}
.pim-close:hover{ background: rgba(0,0,0,.05); color:#111827; }

.pim-body{
  padding: 14px 18px 0;
  overflow: auto;
}
.pim-grid{
  display:grid;
  grid-template-columns: 1;
  gap: 14px;
  padding-bottom: 12px;
}
@media (max-width: 860px){
  .pim-grid{ grid-template-columns: 1fr; }
  .pim-card{ max-height: 90vh; }
}

.pim-section{
  background: #fff;
  border: 1px solid rgba(0,0,0,.08);
  border-radius: 14px;
  padding: 14px;
}
.pim-secTitle{
  display:flex;
  align-items:center;
  gap: 10px;
  margin: 0 0 8px;
  font-size: 1rem;
  color:#111827;
}
.pim-secText{
  margin: 0;
  color:#374151;
  font-size: .95rem;
  line-height: 1.55;
}
.pim-list{
  margin: 10px 0 0;
  padding-left: 18px;
  color:#374151;
  font-size: .95rem;
  line-height: 1.55;
}
.pim-muted{ color:#6b7280; }

.pim-terms{
  margin-top: 12px;
}

.pim-banner{
  display:flex;
  gap: 10px;
  align-items:flex-start;
  padding: 10px 12px;
  border-radius: 12px;
  margin-bottom: 10px;
  border: 1px solid rgba(0,0,0,.08);
  background: #fff7ed;
  color: #7c2d12;
  font-size: .92rem;
}

.pim-termsBox{
  background: #f9fafb;
  border: 1px solid rgba(0,0,0,.08);
  border-radius: 12px;
  padding: 12px;
  max-height: 240px;
  overflow: auto;
  font-size: .92rem;
  line-height: 1.5;
  color:#374151;
  white-space: pre-wrap;
}

.pim-termsMeta{
  display:flex;
  gap: 8px;
  align-items:center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin: 8px 0 0;
  font-size: .85rem;
  color:#6b7280;
}

.pim-inlineLoading{
  display:flex;
  gap: 8px;
  align-items:center;
  font-size: .9rem;
  color:#6b7280;
}

.pim-foot{
  padding: 14px 18px 18px;
  border-top: 1px solid rgba(0,0,0,.08);
  display:flex;
  gap: 10px;
  align-items:center;
  justify-content: space-between;
  flex-wrap: wrap;
  background: #fff;
}
.pim-check{
  display:flex;
  gap: 10px;
  align-items:flex-start;
  color:#111827;
  font-size: .95rem;
}
.pim-check input{ margin-top: 3px; }
.pim-actions{
  display:flex;
  gap: 10px;
  align-items:center;
}
.pim-btn{
  border: 0;
  cursor: pointer;
  border-radius: 12px;
  padding: 10px 14px;
  font-weight: 700;
  font-size: .95rem;
}
.pim-btnPrimary{
  background: #2E7D32;
  color: #fff;
}
.pim-btnPrimary:disabled{
  opacity: .45;
  cursor: not-allowed;
}
.pim-btnGhost{
  background: rgba(0,0,0,.05);
  color: #111827;
}
`;

function normalizeTermsText(raw) {
  if (!raw) return "";
  // Si te llega HTML (a veces pasa), lo dejamos como texto plano para no abrir XSS.
  // Si lo guardas como texto normal en BD, esto no cambia nada.
  return String(raw).replace(/\r\n/g, "\n").trim();
}

export default function PlatformIntroModal({
  open,
  title = "Antes de comenzar",
  subtitle = "Conoce cómo funciona la plataforma y acepta los términos para continuar.",
  accepted,
  onToggleAccepted,
  onContinue,
  onLogout,
  allowClose = false,
  onClose,

  // ✅ NUEVO
  termsLoading = false,
  termsError = "",
  termsContent = "",
  termsVersion = null,
}) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const safeTermsText = useMemo(() => normalizeTermsText(termsContent), [termsContent]);

  if (!open) return null;

  return (
    <div className="pim-overlay" role="dialog" aria-modal="true" aria-labelledby="pim-title">
      <style>{CSS}</style>

      <div className="pim-card">
        <div className="pim-head">
          <div className="pim-titleWrap">
            <div className="pim-badge" aria-hidden="true">
              <Sparkles size={20} />
            </div>

            <div>
              <h2 className="pim-title" id="pim-title">
                {title}
              </h2>
              <p className="pim-sub">{subtitle}</p>
            </div>
          </div>

          {allowClose && (
            <button className="pim-close" onClick={onClose} aria-label="Cerrar">
              <X size={18} />
            </button>
          )}
        </div>

        <div className="pim-body">
          <div className="pim-grid">
            <section className="pim-section">
              <h3 className="pim-secTitle">
                <Info size={18} /> ¿Qué es esta plataforma?
              </h3>
              <p className="pim-secText">
                Es una plataforma de <b>autoaprendizaje</b> donde avanzas por cursos, módulos y actividades a tu propio
                ritmo. Aquí estudiarás con contenido guiado, práctica frecuente y retroalimentación.
              </p>

              <h3 className="pim-secTitle" style={{ marginTop: 12 }}>
                <BookOpen size={18} /> ¿Para qué sirve?
              </h3>
              <ul className="pim-list">
                <li>Reforzar conocimientos con práctica constante y seguimiento de progreso.</li>
                <li>Organizar tu estudio por rutas (niveles → módulos → actividades).</li>
                <li>Prepararte para evaluaciones mediante ejercicios y actividades tipo examen.</li>
              </ul>
            </section>
          </div>

          <section className="pim-section pim-terms">
            <h3 className="pim-secTitle">
              <ShieldCheck size={18} /> Términos y condiciones
            </h3>

            {termsError ? (
              <div className="pim-banner" role="status" aria-live="polite">
                <AlertTriangle size={18} />
                <div>
                  <b>No se pudieron cargar los términos desde el servidor.</b>
                  <div className="pim-muted" style={{ marginTop: 2 }}>
                    {termsError}
                  </div>
                </div>
              </div>
            ) : null}

            {termsLoading ? (
              <div className="pim-inlineLoading" aria-live="polite">
                <Loader2 size={18} className="spin" />
                <span>Cargando términos…</span>
              </div>
            ) : null}

            <div className="pim-termsBox" aria-label="Términos y condiciones">
              {safeTermsText ? (
                safeTermsText
              ) : (
                <span className="pim-muted">
                  No hay términos configurados para registro. Contacta al administrador.
                </span>
              )}
            </div>

            <div className="pim-termsMeta">
              <span>{termsVersion ? `Versión: ${termsVersion}` : "Versión: —"}</span>
              <span className="pim-muted">Debes aceptar para continuar.</span>
            </div>
          </section>
        </div>

        <div className="pim-foot">
          <label className="pim-check">
            <input
              type="checkbox"
              checked={!!accepted}
              onChange={(e) => onToggleAccepted?.(e.target.checked)}
              disabled={termsLoading}
            />
            <span>
              He leído y acepto los <b>términos y condiciones</b>.
            </span>
          </label>

          <div className="pim-actions">
            {onLogout && (
              <button className="pim-btn pim-btnGhost" onClick={onLogout} type="button">
                Cerrar sesión
              </button>
            )}
            <button
              className="pim-btn pim-btnPrimary"
              onClick={onContinue}
              disabled={!accepted || termsLoading || !safeTermsText}
              type="button"
            >
              Continuar
            </button>
          </div>
        </div>
      </div>

      {/* mini CSS para spin */}
      <style>{`
        .spin { animation: pimspin 1s linear infinite; }
        @keyframes pimspin { from { transform: rotate(0deg); } to { transform: rotate(360deg);} }
      `}</style>
    </div>
  );
}
